import {inject, Injectable, OnDestroy} from '@angular/core';
import {BookingProviderDetails} from '../types/booking-provider-details';
import {GtmService} from '../../../services/gtm.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {BookingV2DataService} from './booking-v2-data.service';
import {ToolsService} from '../../../services/tools.service';
import {Router} from '@angular/router';
import {GtmEventEnum} from '../../../enums/GoogleEnums';
import {EventActionEnum, EventCategoryEnum} from '../../../enums/GaTrackingEnums';
import {Subscription, timer} from 'rxjs';
import {BookingState} from '../types/booking-state';
import {BookingV2StateService} from './booking-v2-state.service';
import {BookingV2UrlBuilderService} from './booking-v2-url-builder.service';
import {QueryParams} from '../../../types/QueryParams';
import {ModalsControlService} from '../../../services/modals-control.service';
import {ModalCommand} from '../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {BookingServiceDetail} from '../../../types/BookingServiceDetail';
import {CalendarResponse} from '../types/calendar-response';
import {BookingV2EdgeCasesService} from './booking-v2-edge-cases.service';
import {filter, finalize} from 'rxjs/operators';
import {BookingStaffMember} from '../types/booking-staff-member';
import {SearchState} from '../../../types/SearchState';
import {DefaultValuesService} from '../../../services/default-values.service';
import {RoutesAddonsEnums, RoutesEnums} from '../../../enums/RoutesEnums';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {LoaderService} from '../../../services/loader.service';
import * as dayjs from 'dayjs';
import {AlertStateEnum, BookingStepsEnum} from '../enums/booking-enums';
import {UntypedFormGroup} from '@angular/forms';
import {BookingResponse} from '../types/booking-response';
import {CalendarItem} from '../types/calendar-item';
import {DataLayerParams} from '../../../types/DataLayerParams';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../services/page-views-ga4-resolver.service';
import {DataLayerParamsClickGA4} from '../../../types/DataLayerParamsClickGA4';
import {BookingTypeEnum} from '../../../enums/BookingEnums';
import {GtmGa4EventPushService} from '../../../services/gtm-ga4-event-push.service';

@Injectable({
  providedIn: 'any',
})
export class BookingV2ManagerService implements OnDestroy {
  public initialStep: boolean = true;
  public latestRegisteredPageViewEventState: BookingState;
  private providerDetails: BookingProviderDetails;
  private calendar: CalendarResponse;
  private state: BookingState;
  private subscriptions: Subscription[] = [];
  private debouncedOptionalRegisterPageView: Function;

  private loaderService = inject(LoaderService);
  private loadingItem = this.loaderService.loadingItem;

  constructor(
    private bookingData: BookingV2DataService,
    private bookingState: BookingV2StateService,
    private gtmService: GtmService,
    private urlBuilder: UrlBuilderService,
    private bookingUrlBuilder: BookingV2UrlBuilderService,
    private tools: ToolsService,
    private router: Router,
    private modalsControl: ModalsControlService,
    private bookingEdgeCases: BookingV2EdgeCasesService,
    private defaults: DefaultValuesService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private gtmEventPush: GtmGa4EventPushService,
  ) {
    this.debouncedOptionalRegisterPageView = this.tools.debounce(
      () => {
        if (this.loadingItem() === null) {
          this.optionalRegisterPageView();
        }
      },
      500,
      false,
    );
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  public resetAll(): void {
    this.latestRegisteredPageViewEventState = null;
    this.initialStep = true;
  }

  public closeBooking(): void {
    this.enableBrowserBackButton();
    this.redirectToRefererOrCpruPage();
  }

  public previousStep(): void {
    this.initialStep = false;
    if (this.state.step > 1) {
      const newState: BookingState = this.bookingUrlBuilder.getBaseStateForStep(this.state.step - 1);
      this.navigateToNewState(newState, false);
    }
  }

  public navigateToCareproviderUnit(isCloseAction: boolean) {
    if (!isCloseAction) {
      const gtmTag = {
        event: GtmEventEnum.DEFAULT,
        Category: EventCategoryEnum.VARDEN_BOOKING,
        Action: EventActionEnum.VARDEN_BOOKING_CLICK_CPRU_NAME,
        Label: this.providerDetails.id,
        Value: 1,
      };
      this.gtmService.pushTag(gtmTag);

      const customGa4Params: DataLayerParamsClickGA4 = {
        callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_CLICK_CPRU_NAME}`,
        bookingType: BookingTypeEnum.VARDEN,
        careServiceId: this.state.serviceId,
        careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
        inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
        inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        treatmentId: this.state.selectedBookingServiceId,
        cpruId: this.providerDetails.id,
        pageType: DLPageTypeEnum.BOOKING_PAGE,
      };
      const gtmGa4Tag = {
        event: GtmEventEnum.CTA_CLICK,
        ...customGa4Params,
      };
      this.gtmEventPush.pushClickEvent(gtmGa4Tag);
    }
    const cpruUrl: string = this.buildCpruUrl();
    window.location.assign(cpruUrl);
  }

  public selectStaffMember(staffMember: BookingStaffMember): void {
    this.initialStep = false;
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_SELECT_PRACTITIONER,
      Label: staffMember.id,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SELECT_PRACTITIONER}`,
      bookingType: BookingTypeEnum.VARDEN,
      treatmentId: this.state.selectedBookingServiceId,
      cpruId: this.providerDetails.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const newState: BookingState = this.tools.clone(this.state);
    newState.selectedStaffMemberId = staffMember.id;
    newState.alert = AlertStateEnum.NORMAL;
    this.navigateToNewState(newState, true);
  }

  public selectService(service: BookingServiceDetail, group: number): void {
    this.initialStep = false;
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE,
      Label: service.id,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE}`,
      bookingType: BookingTypeEnum.VARDEN,
      treatmentId: service.id,
      cpruId: this.providerDetails.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    let newState: BookingState = this.bookingUrlBuilder.getBaseStateForStepCalendar();
    newState.selectedBookingServiceId = service.id;
    newState.twoFactorAuthenticationRequired = service.twoFactorAuthenticationRequired;
    newState.serviceGroup = group;
    const staffMembers: Array<BookingStaffMember> = this.bookingData.prepareTherapistsList(service.id);
    const defaultState = this.tools.clone(this.bookingState.getDefaultState());
    newState = this.bookingEdgeCases.getBookingStateUpdatedWithStaffMembersEdgeCases(
      newState,
      defaultState,
      staffMembers,
    );
    this.navigateToNewState(newState, false);
    this.bookingData.resetProfile();
  }

  public selectServiceAndLeaveOtherParamsUntouched(
    serviceId: string,
    group: number,
    twoFactorAuthenticationRequired: boolean,
  ): void {
    this.initialStep = false;
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE,
      Label: serviceId,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE}`,
      bookingType: BookingTypeEnum.VARDEN,
      treatmentId: serviceId,
      cpruId: this.providerDetails.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    let newState: BookingState = this.tools.clone(this.state);
    newState.selectedBookingServiceId = serviceId;
    newState.twoFactorAuthenticationRequired = twoFactorAuthenticationRequired;
    newState.serviceGroup = group;
    const staffMembers: Array<BookingStaffMember> = this.bookingData.prepareTherapistsList(serviceId);
    const defaultState = this.tools.clone(this.bookingState.getDefaultState());
    newState = this.bookingEdgeCases.getBookingStateUpdatedWithStaffMembersEdgeCases(
      newState,
      defaultState,
      staffMembers,
    );
    this.navigateToNewState(newState, false);
    this.bookingData.resetProfile();
  }

  public sendBookingTry(): void {
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SEND_BOOKING_TRY}`,
      bookingType: BookingTypeEnum.VARDEN,
      treatmentId: this.state.selectedBookingServiceId,
      cpruId: this.providerDetails.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  public sendBooking(surveyForm: UntypedFormGroup, invalidFormCallback: Function, phoneNumberFieldKey: string): void {
    this.initialStep = false;
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_SEND_BOOKING,
      Label: `${this.state.selectedBookingServiceId}_${this.providerDetails.name}_${this.providerDetails.id}`,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SEND_BOOKING}`,
      bookingType: BookingTypeEnum.VARDEN,
      treatmentId: this.state.selectedBookingServiceId,
      cpruId: this.providerDetails.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const onBookingSuccessCallback = (result: BookingResponse) => {
      const newState: BookingState = this.bookingUrlBuilder.getBaseStateForStep(BookingStepsEnum.CONFIRM);
      this.navigateToNewState(newState, false);
      const gtmTag = {
        event: GtmEventEnum.DEFAULT,
        Category: EventCategoryEnum.VARDEN_BOOKING,
        Action: EventActionEnum.VARDEN_BOOKING_SUCCESS,
        Label: `${this.state.selectedBookingServiceId}_${this.providerDetails.name}_${this.providerDetails.id}`,
        Value: 1,
      };
      this.gtmService.pushTag(gtmTag);

      const customGa4Params: DataLayerParamsClickGA4 = {
        callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SUCCESS}`,
        bookingType: BookingTypeEnum.VARDEN,
        treatmentId: this.state.selectedBookingServiceId,
        cpruId: this.providerDetails.id,
        careServiceId: this.state.serviceId,
        careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
        inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
        mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        pageType: DLPageTypeEnum.BOOKING_PAGE,
      };
      const gtmGa4Tag = {
        event: GtmEventEnum.CTA_CLICK,
        ...customGa4Params,
      };
      this.gtmEventPush.pushClickEvent(gtmGa4Tag);

      result.closeBookingCallback = () => {
        this.closeBooking();
      };
      // FIXME - backend should return isFromPrice field
      result.isFromPrice = this.bookingData.getServiceById(this.state.selectedBookingServiceId).isFromPrice;
      const modalCommand: ModalCommand = {
        type: ModalTypeEnum.VARDEN_BOOKING_CONFIRMATION_V2,
        data: result,
      };
      timer(1000).subscribe(() => {
        this.setBrowserBackButtonActionToCloseBooking();
        this.modalsControl.openModal(modalCommand);
      });
    };

    this.bookingData.checkFormAndSendBooking(
      surveyForm,
      phoneNumberFieldKey,
      onBookingSuccessCallback,
      invalidFormCallback,
    );
  }

  public selectTimeslot(day: string, time: string): void {
    this.initialStep = false;
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_SELECT_TIMESLOT,
      Label: `${day} ${time}`,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SELECT_TIMESLOT}`,
      bookingType: BookingTypeEnum.VARDEN,
      cpruId: Number(this.providerDetails.id),
      treatmentId: this.state.selectedBookingServiceId,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);

    const newState: BookingState = this.tools.clone(this.state);
    newState.selectedDateTime = `${dayjs(day + time, 'YYYY-MM-DDHH:mm').format(
      this.bookingState.selectedDateTimeStorageFormat,
    )}`;
    newState.step = BookingStepsEnum.SUBMIT;
    this.navigateToNewState(newState, false);
  }

  public navigateToNewState(state: BookingState, replaceUrl: boolean): void {
    const routerLink = this.urlBuilder.CreateInternalBookingRouterLink(
      state.providerId,
      state.serviceId,
      state.timeslot,
    );
    const queryParams: QueryParams = this.bookingUrlBuilder.getBookingQueryParamsFromState(state);
    this.router.navigate(routerLink, {
      queryParams: queryParams,
      replaceUrl: replaceUrl,
    });
  }

  public openTreatmentDetailsModal(service: BookingServiceDetail, group: number): void {
    const modalContent = {
      ...service,
      group: group,
      bookCallback: (selectedService: BookingServiceDetail) => {
        this.selectService(selectedService, group);
        const gtmTag = {
          event: GtmEventEnum.DEFAULT,
          Category: EventCategoryEnum.VARDEN_BOOKING,
          Action: EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE,
          Label: selectedService.id,
          treatmentId: selectedService.id,
          Value: 1,
        };
        this.gtmService.pushTag(gtmTag);
        const customGa4Params: DataLayerParamsClickGA4 = {
          callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_SELECT_SERVICE}`,
          bookingType: BookingTypeEnum.VARDEN,
          cpruId: this.providerDetails.id,
          treatmentId: selectedService.id,
          careServiceId: this.state.serviceId,
          careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
          inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
          inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
          mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
          pageType: DLPageTypeEnum.BOOKING_PAGE,
        };
        const gtmGa4Tag = {
          event: GtmEventEnum.CTA_CLICK,
          ...customGa4Params,
        };
        this.gtmEventPush.pushClickEvent(gtmGa4Tag);
      },
      showBookingButton: false,
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.TREATMENT_DETAILS,
      data: modalContent,
    };
    this.modalsControl.openModal(modalCommand);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.VARDEN_BOOKING,
      Action: EventActionEnum.VARDEN_BOOKING_CLICK_READ_MORE,
      Label: service.id,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_CLICK_READ_MORE}`,
      bookingType: BookingTypeEnum.VARDEN,
      cpruId: this.providerDetails.id,
      treatmentId: service.id,
      careServiceId: this.state.serviceId,
      careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
      inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
      mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
      pageType: DLPageTypeEnum.BOOKING_PAGE,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  public redirectToSearchResults(): void {
    const state: SearchState = this.defaults.getDefaultSearchState(
      this.providerDetails.navigation?.mainCareProviderUnitType?.urlPath,
    );
    state.caregiverUnitTypeUrlPath = this.providerDetails.navigation?.mainCareProviderUnitType?.urlPath;
    state.placeUrlPath = this.providerDetails.navigation?.placeUrlPath;
    const baseUrl: string[] = ['/', RoutesEnums.SEARCH];
    const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(state, baseUrl);
    const queryParams = this.urlBuilder.CreateSearchResultsQueryParams(state);
    this.router.navigate(routerLink, {queryParams});
  }

  public checkAvailableTimesInNext60DaysAndRedirect(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.bookingData
      .getCalendarForNext60Days()
      .pipe(
        finalize(() => {
          this.loaderService.removeFromQueue(queryId);
        }),
      )
      .subscribe(
        (result) => this.handleNextXDaysCalendarData(result, 60),
        (error) => console.error(error),
      );
  }

  public checkAvailableTimesInNext30DaysAndRedirect(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.bookingData
      .getCalendarForNext30Days()
      .pipe(
        finalize(() => {
          this.loaderService.removeFromQueue(queryId);
        }),
      )
      .subscribe(
        (result) => this.handleNextXDaysCalendarData(result, 30),
        (error) => console.error(error),
      );
  }

  private preventBrowserBackButtonFn = (event) => this.popStateListener(event);

  private popStateListener(event) {
    history.pushState(null, null, window.location.href);
    setTimeout(() => {
      this.closeBooking();
    }, 0);
  }

  private setBrowserBackButtonActionToCloseBooking(): void {
    history.pushState(null, null, window.location.href);
    window.addEventListener('popstate', this.preventBrowserBackButtonFn);
  }

  private enableBrowserBackButton(): void {
    window.removeEventListener('popstate', this.preventBrowserBackButtonFn);
  }

  private handleNextXDaysCalendarData(response: CalendarResponse, daysChecked: number): void {
    const newState: BookingState = this.tools.clone(this.state);
    if (response.nextAvailableTime != null && response.nextAvailableTime !== '') {
      const dateOb = dayjs(response.nextAvailableTime);
      newState.alert = AlertStateEnum.NORMAL;
      newState.calendarWeekStartDate = dateOb.startOf('week').format('YYYY-MM-DD');
      newState.calendarWeekEndDate = dateOb.endOf('week').format('YYYY-MM-DD');
    } else {
      const notEmptyDays: Array<CalendarItem> = response.items.filter((item) => item.times.length > 0);
      if (notEmptyDays.length > 0) {
        const dateOb = dayjs(notEmptyDays[0].date);
        newState.alert = AlertStateEnum.NORMAL;
        newState.calendarWeekStartDate = dateOb.startOf('week').format('YYYY-MM-DD');
        newState.calendarWeekEndDate = dateOb.endOf('week').format('YYYY-MM-DD');
      } else {
        const lastItem: CalendarItem = response.items.pop();
        const dateOb = dayjs(lastItem.date);
        newState.calendarWeekStartDate = dateOb.startOf('week').format('YYYY-MM-DD');
        newState.calendarWeekEndDate = dateOb.endOf('week').format('YYYY-MM-DD');
        if (daysChecked === 30) {
          newState.alert = AlertStateEnum.NO_TIMESLOTS_AT_ALL_30;
        }
        if (daysChecked === 60) {
          newState.alert = AlertStateEnum.NO_TIMESLOTS_AT_ALL_90;
        }
      }
    }
    if (!this.tools.isEqual(newState, this.state)) {
      this.navigateToNewState(newState, true);
    }
  }

  private redirectToRefererOrCpruPage(): void {
    const defaultState: BookingState = this.tools.clone(this.bookingState.getDefaultState());
    if (this.state.referral !== defaultState.referral) {
      const url = new URL(this.state.referral, window.location.origin);
      const patch = url.pathname.substr(1);
      const routerLink: string[] = patch.split('/').map((item) => decodeURI(item));
      routerLink.unshift('/');
      const queryParams: QueryParams = {};
      if (url.search !== '') {
        const params = url.search.substr(1).split('&');
        params.forEach((item) => {
          const itemSplit = item.split('=');
          queryParams[itemSplit[0]] = decodeURI(itemSplit[1]);
        });
      }
      this.router.navigate(routerLink, {queryParams});
    } else {
      this.navigateToCareproviderUnit(true);
    }
  }

  private buildCpruUrl(): string {
    let routerLink: string[] = [];
    if (this.tools.isNil(this.providerDetails)) {
      routerLink.push('/');
    } else {
      routerLink = this.urlBuilder.CreateCareproviderUnitRouterLink(
        this.providerDetails.id,
        this.providerDetails.navigation?.urlPath,
      );
    }
    return this.urlBuilder.CreateStaticLink(routerLink);
  }

  private handleCalendarData(calendar: CalendarResponse): void {
    this.calendar = calendar;
    const newState: BookingState = this.bookingEdgeCases.getBookingStateUpdatedWithCalendarEdgeCases(
      calendar,
      this.state.calendarWeekStartDate,
      this.providerDetails,
      this.state,
      this.tools.clone(this.bookingState.getDefaultState()),
      this.bookingState.selectedDateTimeStorageFormat,
    );
    if (!this.tools.isEqual(newState, this.state)) {
      this.navigateToNewState(newState, true);
    }
  }

  private handleProviderData(providerDetails: BookingProviderDetails): void {
    this.providerDetails = providerDetails;
    this.doConditionalRedirectForPreselectedData(providerDetails);
    this.debouncedOptionalRegisterPageView();
  }

  private doConditionalRedirectForPreselectedData(providerDetails: BookingProviderDetails): void {
    let newState = this.tools.clone(this.state);
    const defaultState = this.tools.clone(this.bookingState.getDefaultState());
    newState = this.bookingEdgeCases.getBookingStateUpdatedWithPreselectedOptions(
      newState,
      defaultState,
      providerDetails,
      this.bookingState.selectedDateTimeStorageFormat,
    );
    if (!this.tools.isEqual(newState, this.state)) {
      this.navigateToNewState(newState, true);
    }
  }

  private handleState(state: BookingState): void {
    this.state = state;
    this.debouncedOptionalRegisterPageView();
  }

  private optionalRegisterPageView(): void {
    const defaultState = this.tools.clone(this.bookingState.getDefaultState());
    if (
      !this.tools.isNil(this.providerDetails) &&
      !this.tools.isEqual(this.state, this.latestRegisteredPageViewEventState) &&
      !this.tools.isEqual(this.state, defaultState)
    ) {
      let newState = this.tools.clone(this.state);
      newState = this.bookingEdgeCases.getBookingStateUpdatedWithPreselectedOptions(
        newState,
        defaultState,
        this.providerDetails,
        this.bookingState.selectedDateTimeStorageFormat,
      );
      if (this.tools.isEqual(this.state, newState) || this.initialStep === false) {
        if (this.state.step === BookingStepsEnum.CALENDAR && this.tools.isNil(this.calendar)) {
          return;
        }
        const customParams: DataLayerParams = {
          PageType: DLPageTypeEnum.BOOKING_PAGE,
          BookingIsInitialStep: this.initialStep,
          BookingReferral: this.state.referral,
          CPRUID: this.providerDetails.id,
          CareProviderCategory: !this.tools.isNil(this.providerDetails.navigation?.mainCareProviderUnitType)
            ? this.providerDetails.navigation?.mainCareProviderUnitType?.name
            : '',
        };
        if (this.providerDetails.navigation?.mainCareProviderUnitType) {
          customParams.InferredCareproviderMainTypeID = this.providerDetails.navigation?.mainCareProviderUnitType?.id;

          if (!this.tools.isNil(this.providerDetails.navigation?.mainCareProviderUnitType?.area)) {
            customParams.InferredCareproviderTypesArea =
              this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id;
          }

          if (!this.tools.isNil(this.providerDetails.navigation?.mainCareProviderUnitType?.segment)) {
            customParams.InferredCareproviderTypesSegment =
              this.providerDetails.navigation?.mainCareProviderUnitType?.segment?.id;
          }
        }
        customParams.CPRUID = this.providerDetails.id;

        const customGa4Params: DataLayerParamsPageViewGA4 = {
          pageType: DLPageTypeEnum.BOOKING_PAGE,
          pageTitle: this.bookingData.pageTitle$.getValue(),
          inferredCareProviderTypeAreaId: customParams.InferredCareproviderTypesArea,
          inferredCategoryTypeId: customParams.InferredCareproviderMainTypeID,
          mainCareProviderTypeId: customParams.InferredCareproviderMainTypeID,
          isInitialBookingStep: customParams.BookingIsInitialStep,
          internalBookingFlowStep: this.state.step,
          cpruId: this.state.providerId,
          careServiceId: this.providerDetails.link?.careServiceId,
          careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
          treatmentId: this.state.selectedBookingServiceId,
          timeSlots: this.state.step === BookingStepsEnum.CALENDAR ? this.state.numberOfTimeslots : 0,
          TimeslotMatchExisted: this.state.TimeslotMatchExisted,
        };
        this.registerGa4PageView(customGa4Params);
        this.latestRegisteredPageViewEventState = this.tools.clone(this.state);
      }
    }
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.bookingState.state$.subscribe((result) => this.handleState(result)),
      this.bookingData.providerDetails$
        .pipe(filter((res) => res !== null))
        .subscribe((result) => this.handleProviderData(result)),
      this.bookingData.calendar$
        .pipe(filter((res) => res !== null))
        .subscribe((result) => this.handleCalendarData(result)),
      this.bookingData.noTimeslotError$
        .pipe(filter((res) => res !== null))
        .subscribe((result) => this.handleNoTimeslotErrorStatus(result)),
    );
  }

  private registerGa4PageView(customParams: DataLayerParamsPageViewGA4) {
    this.pageViewGa4Resolver.trackPageView(customParams);
  }

  private handleNoTimeslotErrorStatus(result: boolean) {
    if (result !== null) {
      const customGa4Params: DataLayerParamsClickGA4 = {
        callToAction: `${EventCategoryEnum.VARDEN_BOOKING} - ${EventActionEnum.VARDEN_BOOKING_BACKEND_RESPONSE}`,
        bookingType: BookingTypeEnum.VARDEN,
        cpruId: this.providerDetails.id,
        treatmentId: this.state.selectedBookingServiceId,
        careServiceId: this.state.serviceId,
        careServiceTypeId: this.providerDetails.link?.careServiceTypeId,
        inferredCategoryTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        inferredCareProviderTypeAreaId: this.providerDetails.navigation?.mainCareProviderUnitType?.area?.id,
        mainCareProviderTypeId: this.providerDetails.navigation?.mainCareProviderUnitType?.id,
        Timeslotnotexist: result,
        pageType: DLPageTypeEnum.BOOKING_PAGE,
      };
      const gtmGa4Tag = {
        event: GtmEventEnum.CTA_CLICK,
        ...customGa4Params,
      };
      this.gtmEventPush.pushClickEvent(gtmGa4Tag);
    }
  }
}
